/**
 * Which table filters a left-nav click drops.
 *
 * `useFetchList` keeps each table's filter and sort in session storage
 * for the rest of the day, so a list re-opened later comes back exactly
 * as it was left. That is right when the user walks into a record and
 * back out — losing the filter there would be the bug. It is wrong when
 * they pick the page from the left nav, which reads as "show me this
 * list", and instead they get yesterday's search with no memory of
 * setting it.
 *
 * So the reset lives on the navigation action, not in the table: nothing
 * about `useFetchList`, `baseFilter` or the alerts pre-filter changes,
 * and browser Back still restores the filter because no nav item was
 * clicked. The sidebar already does exactly this for Alerts (see
 * `navigateToAlerts`) — this map extends the same move to the other
 * lists.
 *
 * Only the DAY-TO-DAY lists are listed. Drill-downs that a nav item
 * cannot reach (Claims Manager, BHI, patient-scoped tabs) keep their
 * state, and any path missing from the map is left alone. Alerts is
 * deliberately absent: `navigateToAlerts` clears it itself right before
 * dispatching the pre-filter, and a second writer to the same slot
 * would race it.
 *
 * Not unit-tested on purpose. Every key here is a route reference
 * rather than a literal, so a wrong one throws on import instead of
 * silently matching nothing; a test could only assert the same
 * references back, or assert a mock of them.
 */
import { ALL_ROUTES } from '@app/router';
import { TABLE_KEYS } from 'redux_store';

const FILTERS_BY_LIST_PATH = {
  [ALL_ROUTES.patient.list.path]: [TABLE_KEYS.patientsTable, TABLE_KEYS.patientsDSTable],
  [ALL_ROUTES.assessment.list.path]: [TABLE_KEYS.assessmentsTable, TABLE_KEYS.assessmentsDSTable],
  [ALL_ROUTES.order.list.path]: [TABLE_KEYS.ordersTable, TABLE_KEYS.ordersDSTable],
  [ALL_ROUTES.claim.list.path]: [TABLE_KEYS.claimsTable, TABLE_KEYS.claimsDSTable],
  [ALL_ROUTES.claimReport.list.path]: [
    TABLE_KEYS.claimReportsTable,
    TABLE_KEYS.claimReportsDSTable,
  ],
  [ALL_ROUTES.reportable.list.path]: [TABLE_KEYS.reportableTable, TABLE_KEYS.reportableDSTable],
  [ALL_ROUTES.resource.list.path]: [TABLE_KEYS.resourcesTable, TABLE_KEYS.resourcesDSTable],
  [ALL_ROUTES.botActivity.list.path]: [
    TABLE_KEYS.botActivitiesTable,
    TABLE_KEYS.botActivitiesDSTable,
  ],
  [ALL_ROUTES.program.list.path]: [TABLE_KEYS.programsTable, TABLE_KEYS.programsDSTable],
  [ALL_ROUTES.journey.list.path]: [TABLE_KEYS.journeysTable, TABLE_KEYS.journeysDSTable],
  [ALL_ROUTES.provider.list.path]: [TABLE_KEYS.providersTable],
  [ALL_ROUTES.account.listDS.path]: [TABLE_KEYS.accountsTable],
  [ALL_ROUTES.user.globalManagement.path]: [TABLE_KEYS.usersTable, TABLE_KEYS.usersDSTable],
};

export const listTableFiltersForPath = path => FILTERS_BY_LIST_PATH[path] || [];
